/**
 * Presentation-only experiment: native PDF text-directive highlighting.
 *
 * Uses Chromium's scroll-to-text fragment syntax (`:~:text=`) appended to the
 * existing governed `#page=N` navigation. No retrieval, verification, or page
 * resolution occurs here — only URL fragment assembly for window.open.
 *
 * **Technical limitation:** Native Chromium PDF text fragments are fire-and-forget.
 * Opening a `blob:` PDF via `window.open` provides no callback for highlight
 * success, ambiguity, or miss. A true exact-then-truncated sequential retry is
 * therefore impossible without a controlled viewer (e.g. PDF.js).
 *
 * When a bounded truncated candidate exists, both the exact and truncated
 * directives are included in the open URL. The browser may highlight the first
 * resolvable match among them; this is not programmatic sequential retry.
 *
 * If the browser cannot highlight any passage, navigation to the governed page
 * must still succeed unchanged.
 */

export const GOVERNED_PDF_NATIVE_TEXT_DIRECTIVE_HIGHLIGHT_EXPERIMENT_ID =
  "cognitory_governed_pdf_native_text_directive_highlight_v1" as const;

export const NATIVE_PDF_TEXT_FRAGMENT_HIGHLIGHT_LIMITATION =
  "Native Chromium text fragments support only fire-and-forget highlighting; a true exact-then-truncated retry cannot be detected programmatically without a controlled viewer." as const;

/** Conservative cap per text-directive candidate. */
export const GOVERNED_PDF_TEXT_DIRECTIVE_MAX_PASSAGE_CHARS = 280 as const;

/** Preferred truncated prefix length (120–180 band). */
export const GOVERNED_PDF_TEXT_DIRECTIVE_TRUNCATION_TARGET_CHARS = 150 as const;

/** Minimum truncated candidate length to remain distinctive. */
export const GOVERNED_PDF_TEXT_DIRECTIVE_MIN_TRUNCATED_CHARS = 48 as const;

/** Minimum normalized passage length before a truncated fallback is considered. */
export const GOVERNED_PDF_TEXT_DIRECTIVE_MIN_PASSAGE_FOR_TRUNCATION =
  64 as const;

export type GovernedPdfTextDirectiveCandidates = {
  exact: string | null;
  truncated: string | null;
};

export function normalizeCitedPassageWhitespace(
  citedPassage: string,
): string | null {
  if (citedPassage.includes("\n") || citedPassage.includes("\r")) {
    return null;
  }
  const collapsed = citedPassage.trim().replace(/\s+/g, " ");
  return collapsed.length > 0 ? collapsed : null;
}

/** @deprecated Use normalizeCitedPassageWhitespace + candidate builders. */
export function normalizeCitedPassageForTextDirective(
  citedPassage: string,
): string | null {
  const normalized = normalizeCitedPassageWhitespace(citedPassage);
  if (!normalized) {
    return null;
  }
  if (normalized.length > GOVERNED_PDF_TEXT_DIRECTIVE_MAX_PASSAGE_CHARS) {
    return null;
  }
  return normalized;
}

/**
 * Percent-encode passage text for a text directive match segment.
 * Commas and other directive delimiters must not appear unescaped.
 */
export function encodeTextDirectiveMatchText(passage: string): string {
  return encodeURIComponent(passage);
}

/**
 * Derive exactly one shorter contiguous prefix at a word boundary.
 * Returns null when no sensible truncation exists.
 */
export function deriveTruncatedPassageCandidate(
  normalizedPassage: string,
): string | null {
  if (
    normalizedPassage.length <
    GOVERNED_PDF_TEXT_DIRECTIVE_MIN_PASSAGE_FOR_TRUNCATION
  ) {
    return null;
  }

  let maxEnd = Math.min(
    GOVERNED_PDF_TEXT_DIRECTIVE_TRUNCATION_TARGET_CHARS,
    normalizedPassage.length - 1,
    GOVERNED_PDF_TEXT_DIRECTIVE_MAX_PASSAGE_CHARS,
  );
  if (
    normalizedPassage.length <
    GOVERNED_PDF_TEXT_DIRECTIVE_TRUNCATION_TARGET_CHARS
  ) {
    maxEnd = Math.min(
      normalizedPassage.length - 1,
      Math.max(
        GOVERNED_PDF_TEXT_DIRECTIVE_MIN_TRUNCATED_CHARS,
        Math.floor(normalizedPassage.length * 0.7),
      ),
    );
  }

  if (maxEnd < GOVERNED_PDF_TEXT_DIRECTIVE_MIN_TRUNCATED_CHARS) {
    return null;
  }

  const prefix = normalizedPassage.slice(0, maxEnd);
  const lastSpace = prefix.lastIndexOf(" ");
  if (lastSpace < GOVERNED_PDF_TEXT_DIRECTIVE_MIN_TRUNCATED_CHARS) {
    return null;
  }

  const truncated = normalizedPassage.slice(0, lastSpace).trimEnd();
  if (truncated.length < GOVERNED_PDF_TEXT_DIRECTIVE_MIN_TRUNCATED_CHARS) {
    return null;
  }
  if (truncated.length >= normalizedPassage.length) {
    return null;
  }
  if (!normalizedPassage.startsWith(truncated)) {
    return null;
  }
  if (truncated.length > GOVERNED_PDF_TEXT_DIRECTIVE_MAX_PASSAGE_CHARS) {
    return null;
  }

  return truncated;
}

export function buildGovernedPdfTextDirectiveCandidates(
  citedPassage: string | null | undefined,
): GovernedPdfTextDirectiveCandidates {
  if (citedPassage == null) {
    return { exact: null, truncated: null };
  }

  const normalized = normalizeCitedPassageWhitespace(citedPassage);
  if (!normalized) {
    return { exact: null, truncated: null };
  }

  const exact =
    normalized.length <= GOVERNED_PDF_TEXT_DIRECTIVE_MAX_PASSAGE_CHARS
      ? normalized
      : null;
  const truncated = deriveTruncatedPassageCandidate(normalized);

  if (truncated !== null && truncated === exact) {
    return { exact, truncated: null };
  }

  return { exact, truncated };
}

function buildTextDirectiveFragmentForPassage(passage: string): string {
  return `:~:text=${encodeTextDirectiveMatchText(passage)}`;
}

/**
 * Ordered directive fragments: exact first, then at most one truncated fallback.
 */
export function buildGovernedPdfTextDirectiveFragments(
  citedPassage: string | null | undefined,
): string[] {
  const { exact, truncated } =
    buildGovernedPdfTextDirectiveCandidates(citedPassage);
  const fragments: string[] = [];
  if (exact) {
    fragments.push(buildTextDirectiveFragmentForPassage(exact));
  }
  if (truncated) {
    fragments.push(buildTextDirectiveFragmentForPassage(truncated));
  }
  return fragments;
}

/**
 * Build the `:~:text=` directive body (without leading `#`).
 * Returns null when passage is unsuitable for a deterministic directive.
 */
export function buildGovernedPdfTextDirectiveFragment(
  citedPassage: string | null | undefined,
): string | null {
  const fragments = buildGovernedPdfTextDirectiveFragments(citedPassage);
  return fragments[0] ?? null;
}

export function buildGovernedPdfBlobOpenHash(input: {
  contentType: string;
  pageNumber?: number | null;
  citedPassage?: string | null;
}): string | null {
  if (input.contentType !== "application/pdf") {
    return null;
  }

  const segments: string[] = [];
  const pageNumber = input.pageNumber;
  if (
    typeof pageNumber === "number" &&
    Number.isInteger(pageNumber) &&
    pageNumber > 0
  ) {
    segments.push(`page=${pageNumber}`);
  }

  segments.push(...buildGovernedPdfTextDirectiveFragments(input.citedPassage));

  if (segments.length === 0) {
    return null;
  }

  return `#${segments.join("&")}`;
}
